import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Stack,
  Divider,
  LinearProgress,
  Alert,
  Paper,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useMe } from '../api/hooks.js';
import { api } from '../api/client.js';
import { useT } from '../i18n/useT.js';

/** A package as the API lists it: what it includes, never a price. */
export interface CatalogPlan {
  code: string;
  name: string;
  free: boolean;
  monthlySessions: number | null;
  dailySessions: number | null;
  maxProjects: number | null;
  maxIntegrations: number | null;
  integrationsEnabled: boolean;
  coverOps: string;
  retentionDays: number;
  piiMasking: boolean;
  ingestPiiMasking: boolean;
  ssoEnabled: boolean;
}

export interface UsageLine {
  metric: 'sessions' | 'projects' | 'integrations';
  used: number;
  limit: number | null;
  addon: number;
  level: 0 | 80 | 100;
}

export interface UsageReport {
  period: string;
  plan: { code: string; name: string };
  lines: UsageLine[];
}

type BillingCopy = ReturnType<typeof useT>['billing'];

interface LicenseInfo {
  customer: string;
  tier: string;
  addons: { sessions: number; projects: number; integrations: number; coverOps: boolean };
  expires: string | null;
}

/** The installation's licence standing; `cloud` where there is none to have. */
export type LicenseStatus =
  | { mode: 'cloud' }
  | { mode: 'licensed'; license: LicenseInfo; expiresInDays: number | null }
  | {
      mode: 'grace' | 'stopped';
      cause: 'none' | 'invalid' | 'expired';
      reason?: string;
      license?: LicenseInfo;
      graceEndsAt: string;
      daysLeft: number;
    };

/** Read once an hour: it changes when someone installs a licence, or a day passes. */
export function useLicense() {
  return useQuery({
    queryKey: ['license'],
    queryFn: async () => (await api.get('/v1/license')).data as LicenseStatus,
    staleTime: 60 * 60_000,
    refetchInterval: 60 * 60_000,
  });
}

const day = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });

/** The licence on an on-prem installation: who it is for, what it grants, until when. */
function LicenseCard({ status, t }: { status: Exclude<LicenseStatus, { mode: 'cloud' }>; t: BillingCopy }) {
  const L = t.license;
  const lic = status.license;
  const addons = lic
    ? [
        lic.addons.sessions > 0 && `+${lic.addons.sessions.toLocaleString()} ${t.metric.sessions!.toLowerCase()}`,
        lic.addons.projects > 0 && `+${lic.addons.projects} ${t.metric.projects!.toLowerCase()}`,
        lic.addons.integrations > 0 && `+${lic.addons.integrations} ${t.metric.integrations!.toLowerCase()}`,
        lic.addons.coverOps && 'CoverOps',
      ].filter(Boolean)
    : [];
  const color = status.mode === 'licensed' ? 'success' : status.mode === 'grace' ? 'warning' : 'error';
  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.08em' }}>
          {L.title}
        </Typography>
        <Chip size="small" color={color} label={L.state[status.mode]} />
        {status.mode !== 'licensed' && (
          <Typography variant="caption" color="text.secondary">
            {L.graceEnds(day(status.graceEndsAt))}
          </Typography>
        )}
      </Stack>
      {lic ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, minmax(0, 1fr))' }, gap: 2 }}>
          <Field label={L.customer} value={lic.customer} />
          <Field label={L.package} value={lic.tier.charAt(0).toUpperCase() + lic.tier.slice(1)} />
          <Field label={L.expires} value={lic.expires ? day(lic.expires) : L.noEnd} />
          <Field label={L.addons} value={addons.length ? addons.join(' · ') : L.none} />
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {L.unlicensed}
        </Typography>
      )}
    </Paper>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600} noWrap title={value}>
        {value}
      </Typography>
    </Box>
  );
}

/** The organization's usage against its package; shared with the limit banner. */
export function useUsage(orgId: string | undefined, enabled = true) {
  return useQuery({
    enabled: !!orgId && enabled,
    queryKey: ['usage', orgId],
    queryFn: async () => (await api.get(`/v1/orgs/${orgId}/billing/usage`)).data as UsageReport,
    // The counter moves with every new session; a few minutes is fresh enough
    // for a monthly limit and cheap enough to leave running in the shell.
    refetchInterval: 5 * 60_000,
  });
}

/** One package line in a card. */
function Feature({ on, children }: { on: boolean; children: React.ReactNode }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ opacity: on ? 1 : 0.5 }}>
      {on ? (
        <CheckIcon fontSize="small" color="success" />
      ) : (
        <CloseIcon fontSize="small" sx={{ color: 'text.disabled' }} />
      )}
      <Typography variant="body2">{children}</Typography>
    </Stack>
  );
}

function sessionsLabel(p: CatalogPlan, t: BillingCopy): string {
  if (p.monthlySessions !== null) return t.sessionsPerMonth(p.monthlySessions.toLocaleString());
  if (p.dailySessions !== null) return t.sessionsPerDay(p.dailySessions.toLocaleString());
  return t.unlimitedSessions;
}

function integrationsLabel(p: CatalogPlan, t: BillingCopy): string {
  if (!p.integrationsEnabled || p.maxIntegrations === 0) return t.noIntegrations;
  return p.maxIntegrations === null ? t.unlimitedIntegrations : t.integrations(p.maxIntegrations);
}

/** First day of the next UTC month, the day the session count starts over. */
function resetDate(period: string): string {
  const [y, m] = period.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(y, m, 1)).toLocaleDateString(undefined, { day: 'numeric', month: 'long', timeZone: 'UTC' });
}

/** One limit, as a bar: how much is used, of what, and how full that is. */
function UsageBar({ line, t }: { line: UsageLine; t: BillingCopy }) {
  const unlimited = line.limit === null;
  const notIncluded = line.limit === 0;
  const pct = unlimited || notIncluded ? 0 : Math.min(100, (line.used / line.limit!) * 100);
  const color = line.level === 100 ? 'error' : line.level === 80 ? 'warning' : 'primary';
  return (
    <Box sx={{ minWidth: 0 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
        <Typography variant="body2" fontWeight={600}>
          {t.metric[line.metric]}
        </Typography>
        <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }} color="text.secondary">
          {notIncluded
            ? t.notIncluded
            : unlimited
              ? `${line.used.toLocaleString()} · ${t.unlimited}`
              : `${line.used.toLocaleString()} / ${line.limit!.toLocaleString()}`}
        </Typography>
      </Stack>
      {!unlimited && !notIncluded && (
        <LinearProgress variant="determinate" value={pct} color={color} sx={{ height: 8, borderRadius: 4, my: 0.75 }} />
      )}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minHeight: 24 }}>
        {line.level === 100 && <Chip size="small" color="error" label={t.full} />}
        {line.level === 80 && <Chip size="small" color="warning" label={t.warn} />}
        {line.addon > 0 && (
          <Typography variant="caption" color="text.secondary">
            {t.addon(line.addon.toLocaleString())}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

/**
 * Where the organization stands against its package, and what each package
 * includes. No prices: they are quoted by the team, so every paid card says
 * so and opens a conversation instead of a checkout.
 */
export function BillingPage() {
  const t = useT().billing;
  const { data: me } = useMe();
  const orgId = me?.organizations[0]?.id;

  const plans = useQuery({
    enabled: !!orgId,
    queryKey: ['plans', orgId],
    queryFn: async () => (await api.get(`/v1/orgs/${orgId}/billing/plans`)).data.data as CatalogPlan[],
  });
  const usage = useUsage(orgId);
  const license = useLicense();
  const entitlements = useQuery({
    enabled: !!orgId,
    queryKey: ['entitlements', orgId],
    queryFn: async () =>
      (await api.get(`/v1/orgs/${orgId}/billing/entitlements`)).data as { dailySessions: number | null },
  });

  const current = usage.data?.plan;
  const legacy = current?.code.startsWith('legacy_') ?? false;
  const daily = entitlements.data?.dailySessions ?? null;

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 0.5 }}>
        {t.title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        {t.onPlanPrefix} <b>{current?.name ?? t.free}</b> {t.onPlanSuffix}.
      </Typography>

      {license.data && license.data.mode !== 'cloud' && <LicenseCard status={license.data} t={t} />}

      {legacy && (
        <Alert severity="info" sx={{ mb: 2.5 }}>
          {t.legacyNote}
        </Alert>
      )}

      {usage.data && (
        <Paper variant="outlined" sx={{ p: 2.5, mb: 4 }}>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.08em' }}>
            {t.usageTitle}
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
              gap: { xs: 2, md: 4 },
              mt: 1,
            }}
          >
            {usage.data.lines.map((l) => (
              <UsageBar key={l.metric} line={l} t={t} />
            ))}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            {t.resets(resetDate(usage.data.period))}
            {daily !== null && ` · ${t.dailyCap(daily.toLocaleString())}`}
          </Typography>
        </Paper>
      )}

      <Typography variant="h6" sx={{ mb: 0.5 }}>
        {t.catalogTitle}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t.catalogNote}
      </Typography>

      <Grid container spacing={2} alignItems="stretch">
        {plans.data?.length === 0 && (
          <Typography color="text.secondary" sx={{ ml: 2 }}>
            {t.noPlans}
          </Typography>
        )}
        {(plans.data ?? []).map((p) => {
          const isCurrent = p.code === current?.code;
          return (
            <Grid item xs={12} sm={6} md={4} lg={2.4} key={p.code}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderColor: isCurrent ? 'primary.main' : 'divider',
                  borderWidth: isCurrent ? 2 : 1,
                  position: 'relative',
                }}
              >
                {isCurrent && (
                  <Chip size="small" color="primary" label={t.current} sx={{ position: 'absolute', top: 12, right: 12 }} />
                )}
                <CardContent sx={{ flex: 1 }}>
                  <Typography variant="h6">{p.name}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ my: 1, minHeight: 20 }}>
                    {p.free ? t.free : t.priceOnRequest}
                  </Typography>
                  <Chip label={sessionsLabel(p, t)} size="small" sx={{ mb: 2 }} />
                  <Stack spacing={0.75}>
                    <Feature on>{p.maxProjects === null ? t.unlimitedProjects : t.projects(p.maxProjects)}</Feature>
                    <Feature on={p.integrationsEnabled && p.maxIntegrations !== 0}>{integrationsLabel(p, t)}</Feature>
                    <Feature on>{t.retention(p.retentionDays)}</Feature>
                    <Feature on={p.coverOps !== 'none'}>
                      {p.coverOps === 'addon' ? t.coverOpsAddon : t.coverOpsIncluded}
                    </Feature>
                    <Feature on={p.ingestPiiMasking || p.piiMasking}>{t.masking}</Feature>
                    <Feature on={p.ssoEnabled}>{t.sso}</Feature>
                  </Stack>
                </CardContent>
                <Box sx={{ p: 2, pt: 0 }}>
                  <Divider sx={{ mb: 2 }} />
                  {isCurrent ? (
                    <Button variant="outlined" fullWidth disabled>
                      {t.currentPlan}
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      fullWidth
                      href={`mailto:sales@replayops.ai?subject=${encodeURIComponent(`ReplayOps ${p.name}`)}`}
                    >
                      {t.contactSales}
                    </Button>
                  )}
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
